import React from "react";
import { Badge } from "@/components/ui/badge";
import { Apple, Monitor } from "lucide-react";
import DashboardWidget from "@/components/DashboardWidget";

interface AnalyticsStats {
  trafficSources: {
    utm: Record<string, number>;
  };
  deviceBreakdownBySource: Record<
    string,
    Array<{ type: string; os: string; count: number }>
  >;
}

interface DeviceAnalyticsTableWidgetProps {
  stats: AnalyticsStats | null;
}

export const DeviceAnalyticsTableWidget: React.FC<
  DeviceAnalyticsTableWidgetProps
> = ({ stats }) => {
  const sourceLabels: Record<string, string> = {
    website: "Website Direct",
    facebook: "Facebook",
    facebook_ads: "Facebook Ads",
    instagram: "Instagram",
    instagram_ads: "Instagram Ads",
    tiktok_ads: "TikTok Ads",
    google_organic: "Google Organic",
    google_ads: "Google Ads",
    google_maps: "Google Maps",
    google_maps_ads: "Google Maps Ads",
    whatsapp: "WhatsApp",
    whatsapp_ads: "WhatsApp Ads",
    business_card: "Business Card QR",
    poster: "Shop Poster QR",
    car_door: "Car Door Sign QR",
    window_sticker: "Car Window Sticker",
    flyer: "Flyers",
  };

  // Get all possible sources from UTM data
  const allSources = Object.keys(stats?.trafficSources.utm || {});

  return (
    <DashboardWidget
      id="device-analytics-table"
      title="Device Analytics by Source"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Complete breakdown showing all sources including zero values
        </p>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left p-3 font-semibold">Name</th>
                <th className="text-center p-3 font-semibold w-16">
                  <Apple className="h-4 w-4 mx-auto" />
                </th>
                <th className="text-center p-3 font-semibold w-16">
                  <svg
                    className="h-4 w-4 mx-auto"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M17.523 15.3414c-.5665 0-1.0253-.4588-1.0253-1.0253s.4588-1.0253 1.0253-1.0253 1.0253.4588 1.0253 1.0253-.4588 1.0253-1.0253 1.0253zm-11.046 0c-.5665 0-1.0253-.4588-1.0253-1.0253s.4588-1.0253 1.0253-1.0253 1.0253.4588 1.0253 1.0253-.4588 1.0253-1.0253 1.0253zm11.405-6.02l1.395-2.4157c.077-.1326.0315-.3017-.1011-.3787-.1326-.077-.3017-.0315-.3787.1011L17.4042 8.8495c-.6897-.3157-1.4612-.4912-2.4042-.4912s-1.7145.1755-2.4042.4912L11.208 6.6671c-.077-.1326-.2461-.1781-.3787-.1011-.1326.077-.1781.2461-.1011.3787l1.395 2.4157C10.0618 10.2971 8.535 11.7842 8.535 13.6253v.8439h6.93v-.8439c0-1.8411-1.5268-3.3282-3.465-4.2839zM24 12c0 6.6274-5.3726 12-12 12S0 18.6274 0 12 5.3726 0 12 0s12 5.3726 12 12z" />
                  </svg>
                </th>
                <th className="text-center p-3 font-semibold w-16">
                  <svg
                    className="h-4 w-4 mx-auto"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-13.051-1.351" />
                  </svg>
                </th>
                <th className="text-center p-3 font-semibold w-16">
                  <Monitor className="h-4 w-4 mx-auto" />
                </th>
                <th className="text-center p-3 font-semibold w-20">Total</th>
              </tr>
            </thead>
            <tbody>
              {allSources
                .sort((a, b) => {
                  const totalA = stats?.trafficSources.utm[a] || 0;
                  const totalB = stats?.trafficSources.utm[b] || 0;
                  return totalB - totalA; // Sort by total visitors (highest first)
                })
                .map((source) => {
                  const label =
                    sourceLabels[source] || source.replace("_", " ");
                  const devices =
                    stats?.deviceBreakdownBySource?.[source] || [];
                  const totalVisitors = stats?.trafficSources.utm[source] || 0;

                  // Count devices by type
                  const appleCount = devices
                    .filter(
                      (d) =>
                        d.os.toLowerCase().includes("ios") ||
                        d.os.toLowerCase().includes("mac") ||
                        d.os.toLowerCase().includes("iphone") ||
                        d.os.toLowerCase().includes("ipad"),
                    )
                    .reduce((sum, d) => sum + d.count, 0);

                  const androidCount = devices
                    .filter((d) => d.os.toLowerCase().includes("android"))
                    .reduce((sum, d) => sum + d.count, 0);

                  const windowsCount = devices
                    .filter(
                      (d) =>
                        d.os.toLowerCase().includes("windows") ||
                        d.os.toLowerCase().includes("win"),
                    )
                    .reduce((sum, d) => sum + d.count, 0);

                  const othersCount = devices
                    .filter((d) => {
                      const osLower = d.os.toLowerCase();
                      return (
                        !osLower.includes("ios") &&
                        !osLower.includes("mac") &&
                        !osLower.includes("android") &&
                        !osLower.includes("windows") &&
                        !osLower.includes("win") &&
                        !osLower.includes("iphone") &&
                        !osLower.includes("ipad")
                      );
                    })
                    .reduce((sum, d) => sum + d.count, 0);

                  const hasData = totalVisitors > 0;
                  const rowClass = hasData
                    ? "border-b hover:bg-gray-50"
                    : "border-b bg-gray-50 text-gray-400";

                  return (
                    <tr key={source} className={rowClass}>
                      <td
                        className={`p-3 font-medium ${hasData ? "text-gray-900" : "text-gray-400"}`}
                      >
                        {label}
                      </td>
                      <td
                        className={`text-center p-3 ${hasData ? "text-gray-700" : "text-gray-300"}`}
                      >
                        {appleCount > 0 ? appleCount : "0"}
                      </td>
                      <td
                        className={`text-center p-3 ${hasData ? "text-gray-700" : "text-gray-300"}`}
                      >
                        {androidCount > 0 ? androidCount : "0"}
                      </td>
                      <td
                        className={`text-center p-3 ${hasData ? "text-gray-700" : "text-gray-300"}`}
                      >
                        {windowsCount > 0 ? windowsCount : "0"}
                      </td>
                      <td
                        className={`text-center p-3 ${hasData ? "text-gray-700" : "text-gray-300"}`}
                      >
                        {othersCount > 0 ? othersCount : "0"}
                      </td>
                      <td
                        className={`text-center p-3 font-bold ${hasData ? "text-gray-900" : "text-gray-400"}`}
                      >
                        <Badge
                          variant={hasData ? "default" : "secondary"}
                          className={
                            hasData ? "bg-primary" : "bg-gray-200 text-gray-500"
                          }
                        >
                          {totalVisitors}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        <div className="text-sm text-gray-600">
          <p>
            • Rows with zero visitors are shown in gray and will update when
            data is available
          </p>
          <p>• Data refreshes automatically every 30 seconds</p>
        </div>
      </div>
    </DashboardWidget>
  );
};

export default DeviceAnalyticsTableWidget;
